import React from "react";
import { Link } from "react-router-dom";
import {
  Gauge, ShieldCheck, MicrosoftOutlookLogo, FloppyDiskBack, WifiHigh, Headset, ArrowRight, ArrowUpRight,
} from "@phosphor-icons/react";

const FEATURES = [
  {
    icon: Gauge,
    title: "Managed IT",
    bullets: ["24/7 monitoring", "Unlimited remote support"],
    href: "/managed-it-services",
  },
  {
    icon: ShieldCheck,
    title: "Cybersecurity",
    bullets: ["Endpoint protection", "Ransomware defense", "Email security"],
    href: "/cybersecurity",
  },
  {
    icon: MicrosoftOutlookLogo,
    title: "Microsoft 365",
    bullets: ["User management", "Licensing", "Email migration"],
    href: "/microsoft-365-support",
  },
  {
    icon: FloppyDiskBack,
    title: "Backups",
    bullets: ["Automated cloud backups", "Disaster recovery planning"],
    href: "/business-backup-disaster-recovery",
  },
  {
    icon: WifiHigh,
    title: "Networking",
    bullets: ["Wi-Fi optimization", "Firewall management", "VPN support"],
    href: "/business-wifi",
  },
  {
    icon: Headset,
    title: "On-Site IT",
    bullets: ["Same-day field service", "Server room work", "Emergency response"],
    href: "/onsite-it-support",
  },
];

export default function BusinessFeatures({ onBook }) {
  return (
    <section
      id="business-features"
      data-testid="business-features-section"
      className="bg-white py-20 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
            Business IT
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">
            Business IT that prevents problems
            <span className="block text-blue-600">before they happen.</span>
          </h2>
          <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
            Keep your business productive with proactive IT management. We monitor, secure, back up,
            and maintain your technology so your team can focus on running your business instead of
            dealing with downtime.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to={f.href}
                data-testid={`biz-feature-${i}`}
                className="group relative bg-zinc-50 border border-zinc-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl hover:border-blue-300 hover:bg-white transition-all duration-300"
              >
                <div className="h-11 w-11 rounded-xl bg-blue-600/10 text-blue-600 grid place-items-center">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-zinc-900">{f.title}</h3>
                <ul className="mt-4 space-y-1.5 text-sm text-zinc-700">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                  Learn more
                  <ArrowUpRight size={14} weight="bold" />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={() => onBook && onBook("Free Business IT Assessment")}
            data-testid="biz-features-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 font-semibold blue-glow transition-all"
          >
            Request a Free Business IT Assessment
            <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
}
